import { mapKeys, snakeCase } from 'lodash';
import Actions from '../actions/actions';
import campaignsCRUDActions, {
  CloneSuccessActionType,
} from '../actions/CampaignsCRUDActions';
import {
  CampaignsOverviewCRUDActions,
  campaignsOverviewCRUDActions,
  DeleteSuccessActionType,
  RetrievedAllSuccessActionType,
  RetrievedInputTriggersSuccessActionType,
} from '../actions/CampaignsOverviewCRUDActions';
import {
  campaignFilterMap,
  mapCampaignOverviewKindToUIFilterKind,
} from '../helpers/campaign-overview-helpers';
import {
  CampaignOverviewItemType,
  CampaignOverviewKindType,
  CampaignsOverviewPayload,
  UIFilterCampaignKindType,
} from '../model/CampaignOverviewModel';
import { setBusy, setUnBusy } from './common/commonReducers';

export type UICampaignItem = CampaignOverviewItemType & {
  filterKind: UIFilterCampaignKindType;
};

type RecordValue = CampaignsOverviewPayload & {
  campaigns: UICampaignItem[];
};

type Records = Partial<Record<UIFilterCampaignKindType, RecordValue>>;

export type CampaignsOverviewStateType = {
  records: Records;
  invalidRecords: Partial<Record<UIFilterCampaignKindType, boolean>>;
  activeFilterKind?: UIFilterCampaignKindType;
  counters?: Partial<Record<UIFilterCampaignKindType, number>>;
  enabledInputTriggers: string[];
  busy: boolean;
};

const initial = {
  records: {},
  invalidRecords: {},
  activeFilterKind: undefined,
  counters: undefined,
  enabledInputTriggers: [],
  busy: false,
};

export const campaignsOverview = (
  state: CampaignsOverviewStateType = initial,
  action: any,
) => {
  switch (action.type) {
    case CampaignsOverviewCRUDActions.retrieveAllSuccess: {
      const retrievedAction: RetrievedAllSuccessActionType = action;

      const { botId, kind: queryKind, payload } = retrievedAction;

      const uiFilterKind = mapCampaignOverviewKindToUIFilterKind(queryKind);

      const uiCampaigns: UICampaignItem[] = payload.campaigns.map(item => ({
        ...item,
        filterKind: mapCampaignOverviewKindToUIFilterKind(
          item.scheduleType === 'automation' ? item.scheduleType : item.kind,
        ),
      }));

      const counters = payload.pagination.grouped || {};

      const mappedCounters = mapKeys(counters, (_, k) =>
        mapCampaignOverviewKindToUIFilterKind(
          snakeCase(k) as CampaignOverviewKindType,
        ),
      );

      return {
        ...state,
        counters: {
          ...state.counters,
          ...mappedCounters,
          [uiFilterKind]: payload.pagination?.total,
        },
        records: {
          ...state.records,
          [uiFilterKind]: {
            campaigns: uiCampaigns,
            pagination: payload.pagination,
          },
        },
        invalidRecords: {
          ...state.invalidRecords,
          [uiFilterKind]: false,
        },
        busy: false,
      };
    }
    case CampaignsOverviewCRUDActions.retrieveEnabledInputTriggersSuccess: {
      const retrievedAction: RetrievedInputTriggersSuccessActionType = action;
      const { payload } = retrievedAction;
      return {
        ...state,
        enabledInputTriggers: payload.enabledTriggers,
      };
    }
    case CampaignsOverviewCRUDActions.deleteSuccess: {
      const deleteAction: DeleteSuccessActionType = action;
      const {
        payload: { kind },
      } = deleteAction;
      const uiFilterKind = mapCampaignOverviewKindToUIFilterKind(kind);

      return {
        ...state,
        invalidRecords: {
          ...state.invalidRecords,
          [uiFilterKind]: true,
        },
      };
    }
    case campaignsCRUDActions.cloneSuccessAction:
      const cloneAction: CloneSuccessActionType = action;
      const uiFilterKind = campaignFilterMap.drip;

      const cacheCampaigns = (state.records[uiFilterKind]?.campaigns ||
        []) as UICampaignItem[];

      const campaigns = [
        {
          ...cloneAction.payload,
          filterKind: uiFilterKind,
        } as unknown as UICampaignItem,
        ...cacheCampaigns,
      ];

      return {
        ...state,
        counters: {
          ...state.counters,
          [uiFilterKind]: (state.counters?.[uiFilterKind] || 0) + 1,
        },
        records: {
          ...state.records,
          [uiFilterKind]: {
            campaigns,
            pagination: state.records[uiFilterKind]?.pagination,
          },
        },
        busy: false,
      };
    case campaignsOverviewCRUDActions.busyAction:
      return setBusy(state);
    case CampaignsOverviewCRUDActions.invalidateAll:
    case Actions.RESET:
      return initial;
    case Actions.UNBUSY:
      return setUnBusy(state);
    default:
      return state;
  }
};
